import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateChecklistFormDTO,
  UpdateChecklistFormDTO,
} from "@/modules/checklist/domain/contracts";
import type { ChecklistResultStatsQueryDTO } from "@/modules/checklist/domain/contracts/result";
import type {
  CreateChecklistTypeDTO,
  UpdateChecklistTypeDTO,
} from "@/modules/checklist/domain/contracts/type";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { checklistsApi, checklistTypesApi } from "./client";

export const checklistsKeys = {
  all: ["checklists"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["checklists", "list", q] as const,
  detail: (id: string) => ["checklists", "detail", id] as const,
  executionHistory: (q: {
    limit: number;
    offset: number;
    checklistId?: string;
    departmentId?: string;
    userId?: string;
  }) => ["checklists", "execution-history", q] as const,
  executionDetail: (executionId: string) =>
    ["checklists", "execution-detail", executionId] as const,
  executionStats: (q: ChecklistResultStatsQueryDTO) =>
    ["checklists", "execution-stats", q] as const,
};

export const checklistTypeKeys = {
  all: ["checklist-types"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["checklist-types", "list", q] as const,
  detail: (id: string) => ["checklist-types", "detail", id] as const,
};

export function useChecklistExecutionHistoryQuery(params: {
  limit: number;
  offset: number;
  checklistId?: string;
  departmentId?: string;
  userId?: string;
}) {
  return useQuery({
    queryKey: checklistsKeys.executionHistory(params),
    queryFn: () => checklistsApi.listExecutionHistory(params),
  });
}

export function useChecklistExecutionDetailQuery(executionId: string) {
  return useQuery({
    queryKey: checklistsKeys.executionDetail(executionId),
    queryFn: () => checklistsApi.getExecutionDetail(executionId),
    enabled: !!executionId,
  });
}

export function useChecklistExecutionStats(
  query: ChecklistResultStatsQueryDTO,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: checklistsKeys.executionStats(query),
    queryFn: () => checklistsApi.getExecutionStats(query),
    enabled: options?.enabled ?? true,
  });
}

export function useChecklistsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: checklistsKeys.list(q),
    queryFn: () => checklistsApi.list(q),
  });
}

export function useChecklistQuery(id: string) {
  return useQuery({
    queryKey: checklistsKeys.detail(id),
    queryFn: () => checklistsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateChecklistFormDTO) => checklistsApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງ Checklist ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: checklistsKeys.all });
    },
    onError: () => toast.error("ສ້າງ Checklist ລົ້ມເຫຼວ"),
  });
}

export function useUpdateChecklist(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateChecklistFormDTO) =>
      checklistsApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂ Checklist ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: checklistsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: checklistsKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂ Checklist ລົ້ມເຫຼວ"),
  });
}

export function useDeleteChecklist() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => checklistsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: checklistsKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useChecklistMappings(checklistId: string) {
  return useQuery({
    queryKey: ["checklists", "mappings", checklistId],
    queryFn: () => checklistsApi.getMappings(checklistId),
    enabled: !!checklistId,
  });
}

export function useCreateChecklistMapping(checklistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      sopId?: string | null;
      flowId?: string | null;
      positionId?: string | null;
      departmentId?: string | null;
    }) => {
      if (!checklistId || checklistId.trim() === "") {
        throw new Error("Checklist ID is required");
      }
      return checklistsApi.createMapping(checklistId, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["checklists", "mappings", checklistId],
      });
    },
  });
}

export function useUpdateChecklistMapping(checklistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      mappingId,
      data,
    }: {
      mappingId: string;
      data: {
        sopId?: string | null;
        flowId?: string | null;
        positionId?: string | null;
        departmentId?: string | null;
      };
    }) => checklistsApi.updateMapping(mappingId, data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["checklists", "mappings", checklistId],
      });
    },
  });
}

export function useDeleteChecklistMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mappingId: string) => checklistsApi.deleteMapping(mappingId),
    onSuccess: () => {
      // Invalidate all mappings queries - we don't know which checklist this belongs to
      qc.invalidateQueries({ queryKey: ["checklists", "mappings"] });
    },
  });
}

// KPI mappings (Checklist ↔ KPI — ດຶງຄ່າ KPI ຈາກ checklist execution)
export function useChecklistKpiMappings(checklistId: string) {
  return useQuery({
    queryKey: ["checklists", "kpi-mappings", checklistId],
    queryFn: () => checklistsApi.getKpiMappings(checklistId),
    enabled: !!checklistId,
  });
}

export function useCreateChecklistKpiMapping(checklistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kpiId: string }) =>
      checklistsApi.createKpiMapping(checklistId, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["checklists", "kpi-mappings", checklistId],
      });
    },
  });
}

export function useDeleteChecklistKpiMapping(checklistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mappingId: string) =>
      checklistsApi.deleteKpiMapping(mappingId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["checklists", "kpi-mappings", checklistId],
      });
    },
  });
}

/** ສຳລັບໜ້າ KPI detail — ລາຍການ Checklist ທີ່ດຶງຄ່າມາສູ່ KPI ນີ້ */
export function useChecklistMappingsByKpi(kpiId: string) {
  return useQuery({
    queryKey: ["checklists", "kpi-mappings-by-kpi", kpiId],
    queryFn: () => checklistsApi.getChecklistMappingsByKpi(kpiId),
    enabled: !!kpiId,
  });
}

export function useChecklistTypes(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: checklistTypeKeys.list(query),
    queryFn: () => checklistTypesApi.list(query),
  });
}

export function useChecklistType(id: string) {
  return useQuery({
    queryKey: checklistTypeKeys.detail(id),
    queryFn: () => checklistTypesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateChecklistType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateChecklistTypeDTO) =>
      checklistTypesApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງປະເພດ Checklist ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: checklistTypeKeys.all });
    },
    onError: () => toast.error("ສ້າງປະເພດ Checklist ລົ້ມເຫຼວ"),
  });
}

export function useUpdateChecklistType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateChecklistTypeDTO;
    }) => checklistTypesApi.update(id, input),
    onSuccess: (_, { id }) => {
      toast.success("ແກ້ໄຂປະເພດ Checklist ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: checklistTypeKeys.detail(id) });
      qc.invalidateQueries({ queryKey: checklistTypeKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂປະເພດ Checklist ລົ້ມເຫຼວ"),
  });
}

export function useDeleteChecklistType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => checklistTypesApi.delete(id),
    onSuccess: () => {
      toast.success("ລຶບປະເພດ Checklist ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: checklistTypeKeys.all });
    },
    onError: () => toast.error("ລຶບປະເພດ Checklist ລົ້ມເຫຼວ"),
  });
}
